import React, { useEffect, useMemo, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminPromo.css';

const PERIODS = [7, 30, 90, 365];
const rub = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`;

/** Конструктор ссылки с UTM-метками — чтобы видеть, какой канал приводит пользователей. */
function LinkBuilder() {
    const [base, setBase] = useState('https://app.svodx.ru/login');
    const [source, setSource] = useState('');
    const [medium, setMedium] = useState('');
    const [campaign, setCampaign] = useState('');
    const [copied, setCopied] = useState(false);

    const link = useMemo(() => {
        try {
            const url = new URL(base);
            if (source) url.searchParams.set('utm_source', source.trim());
            if (medium) url.searchParams.set('utm_medium', medium.trim());
            if (campaign) url.searchParams.set('utm_campaign', campaign.trim());
            return url.toString();
        } catch {
            return '';
        }
    }, [base, source, medium, campaign]);

    const copy = async () => {
        try { await navigator.clipboard.writeText(link); setCopied(true); } catch { setCopied(false); }
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="analytics-block">
            <h4>Ссылка с метками</h4>
            <p className="promo-hint">Ставьте такие ссылки в соцсети, рассылки и на лендинг (Tilda) — в таблице выше будет видно, сколько людей пришло и оплатило из каждого канала.</p>
            <div className="promo-grid">
                <label className="promo-field"><span>Куда ведёт</span><input value={base} onChange={(e) => setBase(e.target.value)} /></label>
                <label className="promo-field"><span>Источник (utm_source)</span><input value={source} onChange={(e) => setSource(e.target.value)} placeholder="vk, telegram, tilda" /></label>
                <label className="promo-field"><span>Канал (utm_medium)</span><input value={medium} onChange={(e) => setMedium(e.target.value)} placeholder="post, email, cpc" /></label>
                <label className="promo-field"><span>Кампания (utm_campaign)</span><input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="vesna2026" /></label>
            </div>
            <div className="analytics-link">
                <input readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Ссылка с метками" />
                <button type="button" className="btn btn--secondary" onClick={copy} disabled={!link}>{copied ? 'Скопировано ✓' : 'Копировать'}</button>
            </div>
        </div>
    );
}

function AdminAnalytics() {
    const [days, setDays] = useState(30);
    const [funnel, setFunnel] = useState(null);
    const [sources, setSources] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setFunnel(null);
        setSources(null);
        Promise.all([
            api.get('/admin/analytics/funnel', { params: { days } }),
            api.get('/admin/analytics/sources', { params: { days } }),
        ])
            .then(([f, s]) => {
                setFunnel(f.data.steps);
                setSources(s.data.rows);
                setError('');
            })
            .catch((err) => setError(getApiErrorMessage(err, 'Не удалось загрузить аналитику')));
    }, [days]);

    const top = funnel?.[0]?.count || 0;

    return (
        <section className="admin-main-card promo-admin">
            <div className="promo-admin__head">
                <h2 className="admin-main-card__title">Аналитика</h2>
                <div className="promo-tabs" role="tablist" aria-label="Период">
                    {PERIODS.map((d) => (
                        <button key={d} type="button" role="tab" aria-selected={days === d} className={days === d ? 'is-active' : ''} onClick={() => setDays(d)}>
                            {d === 365 ? 'Год' : `${d} дней`}
                        </button>
                    ))}
                </div>
            </div>
            <div className="promo-admin__body">
                <p className="promo-intro">Пользователи, зарегистрированные за выбранный период: где они останавливаются и откуда пришли. Данные только из базы СВОД, без сторонних счётчиков и cookies.</p>
                {error && <div className="admin-notice admin-notice--error" role="alert">{error}</div>}

                <div className="analytics-block">
                    <h4>Воронка</h4>
                    {!funnel ? <p>Загрузка…</p> : (
                        <div className="funnel">
                            {funnel.map((s, i) => (
                                <div key={s.key} className="funnel__row">
                                    <span className="funnel__label">{s.label}</span>
                                    <span className="funnel__bar"><i style={{ width: `${top ? Math.max((s.count / top) * 100, s.count ? 2 : 0) : 0}%` }} /></span>
                                    <span className="funnel__count"><b>{s.count}</b> <small>{i === 0 ? '' : `${s.pct_of_previous}% от шага выше`}</small></span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="analytics-block">
                    <h4>Источники</h4>
                    {!sources ? <p>Загрузка…</p> : sources.length === 0 ? <p className="admin-modal-empty">За период регистраций не было.</p> : (
                        <div className="admin-table-scroll">
                            <table className="admin-table promo-table">
                                <thead>
                                    <tr><th>Источник</th><th>Канал</th><th>Кампания / код</th><th>Регистраций</th><th>Создали опрос</th><th>Получили ответы</th><th>Оплатили</th><th>Выручка</th></tr>
                                </thead>
                                <tbody>
                                    {sources.map((r) => (
                                        <tr key={`${r.source}|${r.medium}|${r.campaign}`}>
                                            <td><b>{r.source}</b></td>
                                            <td>{r.medium}</td>
                                            <td>{r.campaign}</td>
                                            <td>{r.registrations}</td>
                                            <td>{r.created_poll}</td>
                                            <td>{r.got_responses}</td>
                                            <td>{r.paid} <small className="promo-sub">{r.conversion_pct}%</small></td>
                                            <td>{rub(r.revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <p className="promo-hint">«Не определён» — зарегистрировались до появления отчёта или пришли без меток и внешней ссылки.</p>
                </div>

                <LinkBuilder />
            </div>
        </section>
    );
}

export default AdminAnalytics;
